/**
 * Mic Setup Screen component.
 *
 * One-time pre-recording setup step shown before the first Speaking question.
 * Composes useMicAudioLevel + MicLevelMeter + MicPermissionDenied to:
 *   - Request mic permission on mount if unknown
 *   - Show a live audio-level test when permission is granted
 *   - Show browser-specific recovery instructions when denied
 *   - Gate "Continue" on permission === 'granted'; "Skip" is always available
 *
 * @module MicSetupScreen
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useMicAudioLevel } from '@/hooks/useMicAudioLevel';
import { MicLevelMeter } from './MicLevelMeter';
import { MicPermissionDenied } from './MicPermissionDenied';
import type { MicrophonePermissionState } from '@/hooks/useMicrophonePermission';

interface MicSetupScreenProps {
  /** Current permission state tracked by the parent state machine */
  readonly permissionStatus: MicrophonePermissionState;
  /**
   * Called when the user clicks "Continue" (permission granted) or
   * "Skip mic check". Parent is responsible for the mode transition.
   */
  readonly onContinue: () => void;
  readonly onSkip: () => void;
  /**
   * Called when the screen needs to request mic permission.
   * The parent (useSpeakingModuleState) owns the permission state and
   * updates it; the component re-renders via the permissionStatus prop.
   */
  readonly onRequestPermission: () => Promise<void>;
}

export function MicSetupScreen({
  permissionStatus,
  onContinue,
  onSkip,
  onRequestPermission,
}: MicSetupScreenProps) {
  const { t } = useTranslation();

  // Drive the AnalyserNode loop only when permission is granted
  const { level, isActive } = useMicAudioLevel(permissionStatus === 'granted');

  // On mount, request permission if we don't know yet
  useEffect(() => {
    if (permissionStatus === 'unknown') {
      onRequestPermission();
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isGranted = permissionStatus === 'granted';
  const isDenied = permissionStatus === 'denied';
  const isChecking = permissionStatus === 'unknown';

  return (
    <div className="flex flex-col items-center gap-6 px-2 py-2 w-full">

      {/* Heading */}
      <div className="text-center space-y-1">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground">
          {t('simulation.speaking.micSetup.heading')}
        </h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          {t('simulation.speaking.micSetup.subheading')}
        </p>
      </div>

      {/* Body — switches on permission state */}
      {isChecking && (
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            {t('simulation.speaking.micSetup.requestingAccess')}
          </p>
          <p className="text-xs text-muted-foreground/70 max-w-xs">
            {t('simulation.speaking.micSetup.prePrompt')}
          </p>
        </div>
      )}

      {isGranted && (
        <div className="flex flex-col items-center gap-4 w-full">
          {/* Status chip */}
          <div className="flex items-center gap-1.5 text-xs font-semibold text-green-600 dark:text-green-400">
            <CheckCircle2 className="w-4 h-4" />
            {t('simulation.speaking.micSetup.micReady')}
          </div>

          {/* Live level meter */}
          <div className="w-full bg-muted/30 rounded-xl p-3 sm:p-4">
            <MicLevelMeter
              level={level}
              isActive={isActive}
              className="h-16 sm:h-20"
            />
          </div>

          {/* "Say something" prompt */}
          <p
            className={cn(
              'text-sm font-medium transition-colors duration-300',
              level > 0.08
                ? 'text-primary'
                : 'text-muted-foreground',
            )}
          >
            <Mic className="inline w-3.5 h-3.5 mr-1 align-text-bottom" />
            {t('simulation.speaking.micSetup.saySomething')}
          </p>
        </div>
      )}

      {isDenied && (
        <MicPermissionDenied onRetry={onRequestPermission} />
      )}

      {/* Footer actions */}
      <div className="flex flex-col sm:flex-row items-center gap-3 w-full pt-2">
        <Button
          onClick={onContinue}
          disabled={!isGranted}
          size="lg"
          className="w-full sm:w-auto sm:min-w-[200px] font-bold"
        >
          {t('simulation.speaking.micSetup.continueButton')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onSkip}
          className="text-muted-foreground hover:text-foreground"
        >
          {t('simulation.speaking.micSetup.skipButton')}
        </Button>
      </div>
    </div>
  );
}
